import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';

import Strings from '../../../Strings';
import { actions as patientActions } from '../../SuperUser/Patients/redux/actions';
import Table3 from '../../../containers/Table/Table3';
import { openModalAction } from '../../../actions/modal';
import { actions } from '../redux/actions';
import { CptCodes } from '../../../utils/cmsPrograms';
import { GET_MANDATORY_TASKS_RESULT } from '../redux/constants';
import { weekDays } from '../../../utils';

const emptyCell = <div className="data-loading" />;
const renderWeekDayForTasks = row => {
  if (row.loading) return emptyCell;
  if (!row.configuration?.alarm_week_day) return '-';
  const day = Object.values(weekDays)[row.configuration.alarm_week_day - 1];
  return day ? day.displayName : '-';
}

function MandatoryTasks(props) {
  const { getMandatoryTasks, editMandatoryTasks, getTasks, organizationId } = props;
  const [data, setData] = React.useState(CptCodes.map(c => ({cpt_code: c.code, cpt_code_label: c.label, program: c.program, loading: true})));
  const [tasks, setTasks] = React.useState([]);

  const columnHelper = createColumnHelper();
  const tableRef = React.useRef(null);

  const loadTasksForCode = async (code) => {
    const resp = await getMandatoryTasks({orgId: organizationId, cpt_code: code.cpt_code});
    setData(oldData => {
      const newData = oldData.map(d => {
        if (d.cpt_code === code.cpt_code) {
          if (resp.type === GET_MANDATORY_TASKS_RESULT) {
            const tasksNames = resp?.response.configuration?.task_ids?.map(id => tasks.find(t => t.id === id)?.taskName).filter(n => n).join(', ');
            // The fresh response must win over the old row: after a save the row already
            // holds the previous configuration.
            return {
              ...code,
              ...resp?.response,
              loading: false,
              tasks: tasksNames,
            };
          }
          return { ...d, loading: false };
        }
        return d;
      });
      return newData;
    });
  }

  const load = async () => {
    await Promise.all(
      data.map(async p => {
        loadTasksForCode(p);
      }),
    );
  }

  React.useEffect(() => {
    getTasks(organizationId).then(resp => {
      const tasksData = resp?.response?.data || [];
      setTasks(tasksData);
    });
  }, [organizationId]);

  React.useEffect(() => {
    if (tasks.length === 0) return;
    load();
  }, [organizationId, tasks]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('cpt_code', {
      header: Strings.placeholder.code,
      size: 90,
    }),
    columnHelper.accessor('program', {
      header: Strings.program,
      cell: info => Strings.programs[info.getValue()] || '-',
      size: 100,
    }),
    columnHelper.accessor('tasks', {
      header: Strings.billing.tasks,
      cell: info => info.row.original.loading ? emptyCell : (info.getValue() || '-'),
      size: 1000,
    }),
    columnHelper.accessor('alarm_report_day', {
      header: Strings.dayOfMonth,
      cell: info => {
        const row = info.row.original;
        if (row.loading) return emptyCell;
        return (row.alarm_supported !== false && row.configuration?.alarm_report_day) || '-';
      },
      size: 120,
    }),
    columnHelper.accessor('alarm_week_day', {
      header: Strings.dayOfWeek,
      cell: info => (info.row.original.alarm_supported === false ? '-' : renderWeekDayForTasks(info.row.original)),
      size: 120,
    }),
  ], []);
  
  const buttons = React.useMemo(() => [
    {
      icon: 'edit',
      onClick: (_, code) => editMandatoryTasks({
        code: code.cpt_code,
        configuration: code.configuration,
        alarmSupported: code.alarm_supported,
        orgId: organizationId,
        onSuccess: () => loadTasksForCode(code),
      }),
      text: Strings.edit,
    },
  ]);

  return (
    <Table3
      data={data}
      columns={columns}
      name="ShippingRequests"
      buttons={buttons}
      enableColumnFiltering
      pinFirstColumn
      ref={tableRef}
    />
  );
}

MandatoryTasks.propTypes = {
  getTasks: PropTypes.func.isRequired,
  getMandatoryTasks: PropTypes.func.isRequired,
  editMandatoryTasks: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  getAllTasks: orgId => dispatch(patientActions.getAllTasks(orgId)),
  getTasks: orgId => dispatch(patientActions.getTasks(orgId)),
  getMandatoryTasks: request => dispatch(actions.getMandatoryTasks(request)),
  editMandatoryTasks: data => dispatch(openModalAction('edit-mandatory-tasks', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MandatoryTasks);
